import { memo, useEffect, useMemo, useRef } from 'react'

import { useQuery } from '@muse-web/router'

import { useI18n } from '@muse/i18n'
import { toast } from '@ds/components'
import { Logger } from '@core/logger'
import { PlanType } from '@core/models'
import { stripQueryParams } from '@core/routes'
import { useActions } from '@core/store'
import {
    CHECKOUT_ERROR_CODES,
    CHECKOUT_PARAMS,
    GENERIC_CHECKOUT_ERROR_KEYS,
    STRIPE_SETUP_RETURN_PARAMS,
    getCheckoutErrorKeys,
    getCheckoutErrorText,
    parseCheckoutFlow,
    resolveBillingRedirectAsync,
    resolveSetupIntentRedirectAsync,
    sendCheckoutPaymentErrorEvent,
    sendHoldFailedEvent,
    checkoutStore,
    useCheckoutDialog,
    type TCheckoutFlow,
} from '@features/checkout'
import { useCurrentUser } from '@features/session'
import { useGetBillingProducts, usePlan } from '@features/subscription'

const NO_PRODUCTS: string[] = []

const CHECKOUT_PLANS = [PlanType.Standard, PlanType.Pro]

type TCheckoutRedirect = {
    productCode: string
    setupIntent: string | null
    redirectStatus: string | null
    flow: TCheckoutFlow | undefined
    checkPrepaidCard: boolean
}

export const CheckoutRedirectRunner = memo(() => {
    const { t } = useI18n()
    const { isAuthenticated } = useCurrentUser()
    const query = useQuery<{
        [CHECKOUT_PARAMS.productId]?: string
        [CHECKOUT_PARAMS.flow]?: string
        [CHECKOUT_PARAMS.prepaidCheck]?: string
        [STRIPE_SETUP_RETURN_PARAMS.setupIntent]?: string
        [STRIPE_SETUP_RETURN_PARAMS.redirectStatus]?: string
    }>()
    const productCodeParam = query[CHECKOUT_PARAMS.productId]
    const setupIntentParam = query[STRIPE_SETUP_RETURN_PARAMS.setupIntent]
    const redirectStatusParam = query[STRIPE_SETUP_RETURN_PARAMS.redirectStatus]
    const flowParam = query[CHECKOUT_PARAMS.flow]
    const prepaidCheckParam = query[CHECKOUT_PARAMS.prepaidCheck]

    const redirect = useMemo<TCheckoutRedirect | null>(
        () =>
            productCodeParam
                ? {
                      productCode: productCodeParam,
                      setupIntent: setupIntentParam ?? null,
                      redirectStatus: redirectStatusParam ?? null,
                      flow: parseCheckoutFlow(flowParam),
                      checkPrepaidCard: prepaidCheckParam === '1',
                  }
                : null,
        [productCodeParam, setupIntentParam, redirectStatusParam, flowParam, prepaidCheckParam],
    )

    const productCodes = useMemo(
        () => (redirect && isAuthenticated ? [redirect.productCode] : NO_PRODUCTS),
        [redirect, isAuthenticated],
    )
    const { data: products } = useGetBillingProducts(productCodes)
    const { data: plan } = usePlan()
    const { open: openCheckout } = useCheckoutDialog()
    const { setIsRedirectPending } = useActions({
        setIsRedirectPending: checkoutStore.actions.setIsRedirectPending,
    })

    const processedRedirectRef = useRef<TCheckoutRedirect | null>(null)

    useEffect(() => {
        if (!redirect || processedRedirectRef.current === redirect || !isAuthenticated) {
            return
        }

        setIsRedirectPending(true)

        const product = products?.[0]

        if (!product) {
            return
        }

        if (!product.isPaidTrial && !redirect.setupIntent && !plan?.type) {
            return
        }

        const reportRedirectFailure = (error: unknown) => {
            Logger.error('CheckoutRedirectRunner: the checkout redirect failed', { error })
            toast.error(
                getCheckoutErrorText({
                    title: t(GENERIC_CHECKOUT_ERROR_KEYS.title),
                    description: t(GENERIC_CHECKOUT_ERROR_KEYS.description),
                }),
            )
        }

        processedRedirectRef.current = redirect
        stripQueryParams([
            CHECKOUT_PARAMS.productId,
            CHECKOUT_PARAMS.flow,
            CHECKOUT_PARAMS.prepaidCheck,
            STRIPE_SETUP_RETURN_PARAMS.setupIntent,
            STRIPE_SETUP_RETURN_PARAMS.setupIntentClientSecret,
            STRIPE_SETUP_RETURN_PARAMS.redirectStatus,
        ])

        if (product.isPaidTrial || !CHECKOUT_PLANS.includes(product.plan)) {
            setIsRedirectPending(false)

            return
        }

        if (redirect.setupIntent) {
            if (redirect.redirectStatus !== 'succeeded') {
                setIsRedirectPending(false)

                return
            }

            resolveSetupIntentRedirectAsync({
                setupIntentId: redirect.setupIntent,
                product,
                checkPrepaidCard: redirect.checkPrepaidCard,
            })
                .then(result => {
                    if (result.status === 'success') {
                        return
                    }

                    const keys = getCheckoutErrorKeys(result.code) ?? GENERIC_CHECKOUT_ERROR_KEYS
                    const errorMessage = {
                        title: t(keys.title),
                        description: t(keys.description),
                    }

                    if (result.code === CHECKOUT_ERROR_CODES.cardPreauthFailed) {
                        sendHoldFailedEvent('preauth_check')
                    } else {
                        sendCheckoutPaymentErrorEvent({
                            message: getCheckoutErrorText(errorMessage),
                            code: result.code,
                        })
                    }

                    toast.error(getCheckoutErrorText(errorMessage))
                    openCheckout({
                        product,
                        size: 'default',
                        flow: redirect.flow,
                    })
                })
                .catch(reportRedirectFailure)
                .finally(() => setIsRedirectPending(false))

            return
        }

        if (!plan?.type) {
            setIsRedirectPending(false)

            return
        }

        resolveBillingRedirectAsync({ userPlan: plan.type, product })
            .then(decision => {
                if (decision.kind === 'checkout') {
                    openCheckout({ product, size: 'default' })

                    return
                }

                if (decision.kind === 'portal') {
                    window.location.href = decision.url
                }
            })
            .catch(reportRedirectFailure)
            .finally(() => setIsRedirectPending(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirect, isAuthenticated, products, plan?.type, openCheckout])

    return null
})
